<template>
  <CustomModal
    :visible="visible"
    :width="'35vw'"
    :title="isEditMode ? 'Edit Variable' : 'Create new variable'"
    source="create_edit_dynamic_variable"
    @update:visible="handleClose"
  >
    <div class="flex flex-col gap-4">
      <div class="form_col gap-2">
        <label for="variable_name" class="secondary_color text-[13px]">
          Variable name
        </label>
        <a-input
          id="variable_name"
          v-model:value="formData.variable_name"
          placeholder="variable name"
          class="input_medium"
        />
      </div>

      <div class="form_col gap-2">
        <label for="fallback_value" class="secondary_color text-[13px]">
          Fallback value
        </label>
        <a-input
          id="fallback_value"
          v-model:value="formData.variable_fallback_value"
          placeholder="fallback value"
          class="input_medium"
        />
      </div>
    </div>

    <template #footer>
      <div class="flex flex-row justify-end gap-2">
        <a-button class="secondary_medium" @click="handleClose">
          Cancel
        </a-button>
        <a-button
          class="primary_medium"
          :loading="isSaving"
          :disabled="!isFormValid"
          @click="handleSave"
        >
          {{ isEditMode ? "Save changes" : "Create variable" }}
        </a-button>
      </div>
    </template>
  </CustomModal>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { message } from "ant-design-vue";
import CustomModal from "@/src/components/CustomModal.vue";
import VoiceAgentService from "@/src/api/VoiceAgentService.js";

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  agentId: {
    type: [Number, String],
    required: true,
  },
  variable: {
    type: Object,
    default: null,
  },
  isEditMode: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:visible", "saved"]);

const formData = ref({
  variable_name: "",
  variable_fallback_value: "",
});

const isSaving = ref(false);

const isFormValid = computed(() => {
  try {
    return (
      formData.value.variable_name &&
      formData.value.variable_name.trim() !== ""
    );
  } catch (error) {
    console.error("Error validating form:", error);
    return false;
  }
});

const resetForm = () => {
  try {
    formData.value = {
      variable_name: "",
      variable_fallback_value: "",
    };
  } catch (error) {
    console.error("Error resetting form:", error);
  }
};

const populateForm = () => {
  try {
    if (props.isEditMode && props.variable) {
      formData.value = {
        variable_name: props.variable.variable_name || "",
        variable_fallback_value: props.variable.variable_fallback_value || "",
      };
    } else {
      resetForm();
    }
  } catch (error) {
    console.error("Error populating form:", error);
    resetForm();
  }
};

const handleSave = async () => {
  try {
    if (!isFormValid.value) {
      message.warning("Please fill all required fields");
      return;
    }

    isSaving.value = true;

    const payload = {
      voice_agent_id: props.agentId,
      variable_name: formData.value.variable_name.trim(),
      variable_fallback_value: formData.value.variable_fallback_value || "",
    };

    let response;

    if (props.isEditMode && props.variable?.id) {
      response = await VoiceAgentService.updateDynamicVariable(
        props.variable.id,
        payload
      );
    } else {
      response = await VoiceAgentService.createDynamicVariable(payload);
    }

    if (response?.success) {
      message.success(
        `Variable ${props.isEditMode ? "updated" : "created"} successfully`
      );
      emit("saved");
      handleClose();
    } else {
      message.error(
        `Failed to ${props.isEditMode ? "update" : "create"} variable`
      );
    }
  } catch (error) {
    console.error("Error saving variable:", error);
    message.error(`Failed to ${props.isEditMode ? "update" : "create"} variable`);
  } finally {
    isSaving.value = false;
  }
};

const handleClose = () => {
  try {
    resetForm();
    emit("update:visible", false);
  } catch (error) {
    console.error("Error closing modal:", error);
  }
};

watch(
  () => props.visible,
  (newValue) => {
    if (newValue) {
      populateForm();
    }
  },
  { immediate: true }
);
</script>

